/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  InlineNotification,
  Link,
  TextInput,
  Tooltip,
  RadioButtonGroup,
  RadioButton
} from 'carbon-components-react';
import cx from 'classnames';
import _ from 'lodash';
import { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import {
  isFeatureEnabled,
  selectIsConversationalSearchEnabled,
  selectIsInsightActionsEnabled
} from '@ibm-watson/shared/modules/ia/store/features/selectors';
import { useAppSelector } from '../../../../store';
import { FeatureFlag } from '../../../../ts/feature-flags';
import { MultiTurnType } from '../../../../ts/skill';
import { ConfigOption } from '../../../discovery/components/search-detail-config/config-option/ConfigOption';
import { links } from '../../../discovery/components/search-detail-config/links';
import { messages as ConfigOptionMessages } from '../../../discovery/components/search-detail-config/messages';
import { ConfigureContentMessages as configContentMessages } from '../../../elastic-search/components/messages';
import { selectInstanceRegion } from '../../store/session/selectors';
import { isCpd, isWxoEnabled } from '../../util/config';
import { Regions } from '../../util/regions';
import BetaTag from '../beta-tag/BetaTag';
import { Citations } from '../citations/Citations';
import { conversationalSearchGALinks } from './links';
import { SearchConfiguration } from './SearchConfiguration';

export interface ConversationalSearchSectionProps {
  draft: {
    setCitationsTitleValue: (string) => { payload: any; type: string };
    setMaxCitationsValue: (number) => void;
    setIsConversationalSearchEnabled: (string) => {
      payload: any;
      type: string;
    };
    isConversationalSearchEnabled: boolean;
    setConversationalSearchRetrievalConfidenceThreshold: (string) => void;
    conversationalSearchRetrievalConfidenceThreshold: string;
    setConversationalSearchConfidenceThreshold: (string) => void;
    conversationalSearchConfidenceThreshold: string;
    setConversationalSearchVerbosityOption: (string) => void;
    conversationalSearchVerbosityOption: string;
    setConversationalSearchResponseConfidenceThreshold: (string) => void;
    conversationalSearchResponseConfidenceThreshold: string;
    citationsTitleValue: string;
    maxCitationsValue: number;
    setConversationalSearchMultiTurn: (boolean) => void;
    isConversationalSearchMultiTurnEnabled: boolean;
  };
  readOnly: boolean;
}

export const ConversationalSearchSection = ({
  draft,
  readOnly
}: ConversationalSearchSectionProps) => {
  const { formatMessage: t } = useIntl();
  const [invalidCitationsTitle, setInvalidCitationsTitle] = useState(false);
  const [invalidCitationsTitleText, setInvalidCitationsTitleText] =
    useState('');
  const [multiTurnOption, setMultiTurnOption] = useState(
    draft.isConversationalSearchMultiTurnEnabled
      ? MultiTurnType.MULTI_TURN
      : MultiTurnType.SINGLE_TURN
  );
  const isSapConversationalSearchEnabled = useAppSelector<boolean>(state =>
    isFeatureEnabled(state, FeatureFlag.SAP_CONVERSATIONAL_SEARCH)
  );

  const isMultiTurnConversationalSearchEnabled = useAppSelector<boolean>(
    state =>
      isFeatureEnabled(state, FeatureFlag.IA_CONVERSATIONAL_SEARCH_MULTI_TURN)
  );

  const isInsightActionsEnabled = useAppSelector<boolean>(
    selectIsInsightActionsEnabled
  );
  const instanceRegion = useAppSelector(selectInstanceRegion);
  const frankfurtCustomer = instanceRegion === Regions.EU_DE;

  const conversationalSearchToggleVisible = useAppSelector(
    selectIsConversationalSearchEnabled
  );

  const determineConversationalSearchTitle = () => {
    if (isCpd) {
      return ConfigOptionMessages.conversationalSearchTitleCPD;
    }

    if (frankfurtCustomer) {
      return ConfigOptionMessages.conversationalSearchTitleFrankfurt;
    }

    return ConfigOptionMessages.conversationalSearchTitle;
  };

  const onCitationsTitleChange = e => {
    const title = e?.target?.value ?? '';
    if (title === null || _.isEmpty(title?.trim())) {
      setInvalidCitationsTitle(true);
      setInvalidCitationsTitleText(
        t(
          ConfigOptionMessages.conversationalSearchCitationsTitleInvalidEmptyValueText
        )
      );
    } else {
      setInvalidCitationsTitle(false);
      setInvalidCitationsTitleText('');
    }
    draft.setCitationsTitleValue(title);
  };

  const onToggleConversationalSearchEnabled = () => {
    draft.setIsConversationalSearchEnabled(
      !draft.isConversationalSearchEnabled
    );

    if (!isInsightActionsEnabled) {
      draft.setConversationalSearchConfidenceThreshold(
        draft.conversationalSearchConfidenceThreshold
      );
    } else {
      draft.setConversationalSearchRetrievalConfidenceThreshold(
        draft.conversationalSearchRetrievalConfidenceThreshold
      );
      draft.setConversationalSearchResponseConfidenceThreshold(
        draft.conversationalSearchResponseConfidenceThreshold
      );
    }

    draft.setConversationalSearchVerbosityOption(
      draft.conversationalSearchVerbosityOption
    );

    if (multiTurnOption === MultiTurnType.MULTI_TURN) {
      draft.setConversationalSearchMultiTurn(true);
    } else {
      draft.setConversationalSearchMultiTurn(false);
    }
  };

  const convoSearchEnabledReadonly =
    !draft.isConversationalSearchEnabled || readOnly;

  const toggleView = evt => {
    const { value } = evt.target;
    if (value === MultiTurnType.SINGLE_TURN) {
      setMultiTurnOption(MultiTurnType.SINGLE_TURN);
      draft.setConversationalSearchMultiTurn(false);
    } else {
      setMultiTurnOption(MultiTurnType.MULTI_TURN);
      draft.setConversationalSearchMultiTurn(true);
    }
  };

  return conversationalSearchToggleVisible ? (
    <div data-floating-menu-container className="ConversationalSearchSection">
      {!isCpd && (
        <InlineNotification
          lowContrast
          kind="info"
          subtitle={t(ConfigOptionMessages.pricingAndTermsInfo, {
            PricingLink: (
              <Link
                href={
                  isWxoEnabled
                    ? conversationalSearchGALinks.pricing.wxo
                    : conversationalSearchGALinks.pricing.ibmCloud
                }
                target="_blank"
                rel="noopener noreferrer">
                {t(configContentMessages.pricing)}
              </Link>
            ),
            TermsLink: (
              <Link
                href={
                  isWxoEnabled
                    ? conversationalSearchGALinks.terms.wxo
                    : conversationalSearchGALinks.terms.ibmCloud
                }
                target="_blank"
                rel="noopener noreferrer">
                {t(configContentMessages.terms)}
              </Link>
            )
          })}
          title=""
        />
      )}
      <ConfigOption
        header={t(ConfigOptionMessages.conversationalSearch)}
        description={t(determineConversationalSearchTitle())}
        learnMore={links.CONVERSATION_RESPONSE_LEARN_MORE_LINK}
        toggleAriaLabel={t(ConfigOptionMessages.conversationalSearchLabel)}
        toggled={draft.isConversationalSearchEnabled}
        onToggle={onToggleConversationalSearchEnabled}
        beta={false}>
        <>
          {isMultiTurnConversationalSearchEnabled && (
            <RadioButtonGroup
              className="ConfigureContent__radio-button-group"
              name="conversational-search-multi-turn"
              defaultSelected={
                draft.isConversationalSearchMultiTurnEnabled
                  ? MultiTurnType.MULTI_TURN
                  : MultiTurnType.SINGLE_TURN
              }
              legendText={t(ConfigOptionMessages.conversationTurns)}
              orientation="vertical">
              <RadioButton
                data-testid="ConfigureContent__conversational_search_single_turn"
                labelText={
                  <FormattedMessage
                    {...ConfigOptionMessages.conversationSingleTurn}
                    values={{ strong: chunk => <strong>{chunk}</strong> }}
                  />
                }
                id={MultiTurnType.SINGLE_TURN}
                value={MultiTurnType.SINGLE_TURN}
                onClick={toggleView}
                checked={multiTurnOption === MultiTurnType.SINGLE_TURN}
                disabled={convoSearchEnabledReadonly}
              />
              <div
                className={cx('ConfigureContent__multiturnDescription', {
                  'ConfigureContent__multiturnDescription--disabled':
                    convoSearchEnabledReadonly
                })}>
                <FormattedMessage
                  {...ConfigOptionMessages.conversationSingleTurnDescription}
                />
              </div>
              <div className="ConfigureContent__conversationalSearchMultiTurn">
                <RadioButton
                  data-testid="ConfigureContent__conversational_search_multi_turn"
                  labelText={
                    <FormattedMessage
                      {...ConfigOptionMessages.conversationMultiTurn}
                      values={{ strong: chunk => <strong>{chunk}</strong> }}
                    />
                  }
                  id={MultiTurnType.MULTI_TURN}
                  value={MultiTurnType.MULTI_TURN}
                  onClick={toggleView}
                  checked={multiTurnOption === MultiTurnType.MULTI_TURN}
                  disabled={convoSearchEnabledReadonly}
                />
                <BetaTag className="ConfigureContent__conversationalSearchMultiTurn--beta-tag" />
              </div>
              <div
                className={cx('ConfigureContent__multiturnDescription', {
                  'ConfigureContent__multiturnDescription--disabled':
                    convoSearchEnabledReadonly
                })}>
                <FormattedMessage
                  {...ConfigOptionMessages.conversationMultiTurnDescription}
                />
              </div>
            </RadioButtonGroup>
          )}
          {/* SAP Conversational Search uses a text response instead of the conversational_search response type, so we should not show
                the option for changing the citations title since it won't apply to them */}
          {!isSapConversationalSearchEnabled && (
            <div
              className={cx(
                'ConfigureContent__conversationalSearchCitationsTitle-container',
                {
                  'ConfigureContent__conversationalSearchCitationsTitle-container-multiturn':
                    isMultiTurnConversationalSearchEnabled
                }
              )}>
              <TextInput
                autoComplete="off"
                id="ConfigureContent__conversationalSearchCitationsTitle-input"
                invalid={invalidCitationsTitle}
                invalidText={invalidCitationsTitleText}
                placeholder={t(
                  ConfigOptionMessages.conversationalSearchCitationsTitlePlaceholder
                )}
                // TODO ES: Will need this to respect access control in the future.
                // readOnly={false}
                labelText={
                  <Tooltip
                    align="start"
                    triggerText={t(
                      ConfigOptionMessages.conversationalSearchCitationsTitleLabel
                    )}
                    tabIndex={0}>
                    {t(
                      ConfigOptionMessages.conversationalSearchCitationsTitleTooltip
                    )}
                  </Tooltip>
                }
                onChange={onCitationsTitleChange}
                disabled={convoSearchEnabledReadonly}
                size="md"
                value={draft.citationsTitleValue}
                type="text"
              />
            </div>
          )}
          <SearchConfiguration draft={draft} readOnly={readOnly} />
        </>
      </ConfigOption>
      <Citations draft={draft} readOnly={readOnly} />
    </div>
  ) : null;
};
